import React, { useCallback, useEffect, useState } from "react"
import {
    Dialog,
    DialogHeader,
    DialogDescription,
    Grid,
    Button,
    useIntl,
    useToast,
    ToastLevel,
    DialogActions,
    Tab,
    TabProps,
} from "@metsooutotec/modes-react-components"
import { StoreResults } from "../../../../../stores/stores-helper"
import sensorStore from "features/iot/sensors/stores/sensor-store"
import SensorInputFields, { SensorFormField } from "../../utils/sensor-input-fields"
import SensorDateInputField from "./fields/sensor-date-input-field"
import { useCSVImport } from "./utils/use-csv-import"
import { useSensorForm } from "./fields/use-sensor-form"
import SensorCsvInputField from "./fields/sensor-csv-input-filed"
import { handleUploadFileClick } from "components/utils/modal-helper"
import { SBSensor } from "features/iot/sensor-batch/models/sensor-batch-models"
import { convertDateToDDMMMYYYY } from "components/utils/date-formatter-utils"
import { validateDecimal, validateHexadecimal16Digit, validateHours, validateInteger, validateText } from "validators/inputValidators"
import { SensorCSVRow } from "features/iot/sensors/models/sensor-models"

interface SensorModalProps {
    isOpen: boolean
    onClose: (passed: boolean) => void
    sensor?: SBSensor
    sensorBatchId: string
}

const SensorImportModal: React.FC<SensorModalProps> = ({ isOpen, onClose, sensor, sensorBatchId }) => {
    const { formatMessage } = useIntl()
    const { setMessage } = useToast()
    const { setFormData, formData, formErrors, handleChange, handleValidation, isEditing, resetForm, validateFormFields, clearUploadCSVName } =
        useSensorForm(sensor)
    const { setSelectedCSV, setIsError, sensorListDto, isError, clearCSV } = useCSVImport()

    const [isManufactured, setIsManufactured] = useState<boolean>(false)
    const [manufacturedDate, setManufacturedDate] = useState<string>("")
    const [datePickerError, setDatePickerError] = useState<{ isError: boolean; errorMessage: string }>({ isError: false, errorMessage: "" })
    const [isLoading, setIsLoading] = useState<boolean>(false)
    const [activeTabIndex, setActiveTabIndex] = useState<number>(0)

    const handleIsManufacturedToggle = () => setIsManufactured(!isManufactured)

    const handleDateChange = useCallback((value: string) => {
        try {
            setDatePickerError({ isError: false, errorMessage: "" })
            const date = new Date(value)
            if (isNaN(date.getDate())) {
                setDatePickerError({ isError: true, errorMessage: "Invalid Date!" })
            } else {
                setManufacturedDate(convertDateToDDMMMYYYY(date))
            }
        } catch {
            setDatePickerError({ isError: true, errorMessage: "Error while setting Date" })
        }
    }, [])

    useEffect(() => {
        setIsManufactured(sensor?.isManufactured ?? false)
        if (sensor?.isManufactured) {
            setManufacturedDate(convertDateToDDMMMYYYY(sensor.manufacturedDate as Date))
        }
    }, [sensor])

    useEffect(() => {
        if (isError.isError) setIsError({ isError: false, errorMessage: "" })
    }, [formData])

    const mapFormDataToSensorCSVRow = (formData: Record<string, string>): SensorCSVRow => ({
        serialNumber: formData.serialNumber,
        macAddress: formData.macAddress,
        isManufactured: isManufactured,
        manufacturedDate: manufacturedDate || null,
        offset: parseFloat(formData.offset),
        pitch: parseFloat(formData.pitch),
        numberOfResistiveElements: parseInt(formData.numberOfResistiveElements),
        hardwareRevision: formData.hardwareRevision,
        firmwareRevision: formData.firmwareRevision,
    })
    const handleFormSubmit = async () => {
        const isValid = validateFormFields()
        if (!isValid) {
            setIsError({ isError: true, errorMessage: "Please fix the validation errors in the form." })
            return
        }

        try {
            setIsLoading(true)
            const requestResult: StoreResults = isEditing
                ? await sensorStore.editEntity({ sensorBatchId, ...mapFormDataToSensorCSVRow(formData) })
                : await sensorStore.createEntity({ sensorBatchId, sensors: [mapFormDataToSensorCSVRow(formData)] })

            if (requestResult.passed) {
                setMessage(`Sensor ${isEditing ? "updated" : "created"} successfully`, ToastLevel.SUCCESS, 6000)
                onClose(true)
                resetForm()
            } else {
                setIsError({ isError: true, errorMessage: requestResult.message })
            }
        } catch (error) {
            setIsError({ isError: true, errorMessage: "Error occurred during submission." })
        } finally {
            setIsLoading(false)
        }
    }

    const handleCSVSubmit = async () => {
        if (sensorListDto.length === 0) {
            setIsError({ isError: true, errorMessage: "Please upload a CSV file." })
            return
        }
        try {
            setIsLoading(true)
            const requestResult = await sensorStore.createEntity({ sensorBatchId, sensors: sensorListDto })
            if (requestResult.passed) {
                setMessage("Sensors created from CSV!", ToastLevel.SUCCESS, 6000)
                onClose(true)
                resetForm()
            } else {
                setIsError({ isError: true, errorMessage: requestResult.message })
            }
        } catch {
            setIsError({ isError: true, errorMessage: "Submission failed." })
        } finally {
            setIsLoading(false)
        }
    }

    const inputStrings: SensorFormField[] = [
        {
            id: "serialNumber",
            label: formatMessage("sensorsModal.serialNumber"),
            placeholder: `${formatMessage("keyWord.Enter")} ${formatMessage("sensorsModal.serialNumber")}`,
            type: "text",
            isRequired: true,
            validate: validateText,
        },
        {
            id: "macAddress",
            label: formatMessage("sensorsModal.macAddress"),
            placeholder: `${formatMessage("keyWord.Enter")} ${formatMessage("sensorsModal.macAddress")}`,
            type: "text",
            isRequired: true,
            validate: validateHexadecimal16Digit,
            disabled: isEditing,
        },
        {
            id: "offset",
            label: formatMessage("sensorsModal.offset"),
            placeholder: `${formatMessage("keyWord.Enter")} ${formatMessage("sensorsModal.offset")}`,
            type: "text",
            isRequired: true,
            validate: validateHours,
        },
        {
            id: "numberOfResistiveElements",
            label: formatMessage("sensorsModal.numberOfResistiveElements"),
            placeholder: `${formatMessage("keyWord.Enter")} ${formatMessage("sensorsModal.numberOfResistiveElements")}`,
            type: "text",
            isRequired: true,
            validate: validateInteger,
        },
        {
            id: "pitch",
            label: formatMessage("sensorsModal.pitch"),
            placeholder: `${formatMessage("keyWord.Enter")} ${formatMessage("sensorsModal.pitch")}`,
            type: "text",
            isRequired: true,
            validate: validateDecimal,
        },
        {
            id: "hardwareRevision",
            label: formatMessage("sensorsModal.hardwareRevision"),
            placeholder: `${formatMessage("keyWord.Enter")} ${formatMessage("sensorsModal.hardwareRevision")}`,
            type: "text",
            isRequired: true,
            validate: validateText,
        },
        {
            id: "firmwareRevision",
            label: formatMessage("sensorsModal.firmwareRevision"),
            placeholder: `${formatMessage("keyWord.Enter")} ${formatMessage("sensorsModal.firmwareRevision")}`,
            type: "text",
            isRequired: true,
            validate: validateText,
        },
    ]
    const panes: TabProps["panes"] = [
        {
            menuItem: {
                key: "form",
                content: formatMessage("sensorsModal.addSingleSensor"),
                as: "button",
            },
            render: () => (
                <Grid columns={2}>
                    <SensorInputFields
                        formData={formData}
                        formErrors={formErrors}
                        handleChange={handleChange}
                        handleValidation={handleValidation}
                        onClearClick={inputId => handleChange(inputId, "")}
                        inputStrings={inputStrings}
                    />
                    <SensorDateInputField
                        isManufactured={isManufactured}
                        manufacturedDate={manufacturedDate}
                        handleIsManufacturedToggle={handleIsManufacturedToggle}
                        handleDateChange={handleDateChange}
                        datePickerError={datePickerError}
                        labelManufactured={formatMessage("headings.isManufactured")}
                        labelManufacturedDate={formatMessage("headings.manufacturedDate")}
                    />
                </Grid>
            ),
        },
        !isEditing
            ? {
                  menuItem: {
                      key: "csv",
                      content: formatMessage("sensorsModal.AddMultipleSensors"),
                      as: "button",
                  },
                  render: () => (
                      <Grid columns={2}>
                          <SensorCsvInputField
                              formData={formData}
                              handleChange={handleChange}
                              onFileUpload={() => handleUploadFileClick(".csv", setSelectedCSV, setFormData, null, "uploadCSVName")}
                              clearCSV={() => clearCSV(clearUploadCSVName)}
                              inputCsv={[
                                  {
                                      id: "uploadCSVName",
                                      label: formatMessage("sensorsModal.CSVFile"),
                                      placeholder: formatMessage("sensorsModal.CSVFile"),
                                  },
                              ]}
                          />
                      </Grid>
                  ),
              }
            : undefined,
    ].filter(Boolean) as TabProps["panes"]

    return (
        <Dialog dimmer open={isOpen} size='small'>
            <DialogHeader>{isEditing ? formatMessage("sensorModal.editTitle") : formatMessage("sensorModal.importTitle")}</DialogHeader>
            {isError.isError && <p style={{ color: "red" }}>{isError.errorMessage}</p>}
            <DialogDescription style={{ marginBottom: "var(--size-xxl)" }}>
                {isEditing ? formatMessage("sensorModal.editDescription") : formatMessage("sensorModal.importDescription")}
            </DialogDescription>

            <Tab
                as='div'
                data-cy='tab'
                defaultActiveIndex={0}
                onTabChange={(e, data) => setActiveTabIndex(data.activeIndex as number)}
                menu={{ pointing: true, secondary: true }}
                panes={panes}
                style={{ marginBottom: "0.5em" }}
            />

            <DialogActions>
                <Button
                    loading={isLoading}
                    disabled={isLoading}
                    onClick={activeTabIndex === 0 ? handleFormSubmit : handleCSVSubmit}
                    children={formatMessage("modal.confirm")}
                    primary
                    flexible
                    centered
                />
                <Button
                    onClick={() => {
                        onClose(false)
                        resetForm()
                        setIsError({ isError: false, errorMessage: "" })
                    }}
                    children={formatMessage("modal.cancel")}
                    primary={false}
                    flexible
                    centered
                />
            </DialogActions>
        </Dialog>
    )
}

export default SensorImportModal
